import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Image as ImageIcon } from "lucide-react";
import { MovementTypeBadge } from "./MovementTypeBadge";
import { StockJournalImagesModal } from "./StockJournalImagesModal";
import { GreyTooltip } from "./GreyTooltip";
import { TablePagination } from "../../leave-management/components/TablePagination";
import { ui } from "../../../components/ui";
import { cn } from "../../../lib/utils";

const formatPrice = (val) => {
  if (val === null || val === undefined || val === "") return "—";
  const n = Number(val);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(n);
};

const MONTH_KEYS = [
  "dateTime.monthJan",
  "dateTime.monthFeb",
  "dateTime.monthMar",
  "dateTime.monthApr",
  "dateTime.monthMay",
  "dateTime.monthJun",
  "dateTime.monthJul",
  "dateTime.monthAug",
  "dateTime.monthSep",
  "dateTime.monthOct",
  "dateTime.monthNov",
  "dateTime.monthDec",
];

/** Stack multi-word header labels vertically to save horizontal space. */
function StackedHeader({ label, align = "left" }) {
  const words = String(label).trim().split(/\s+/).filter(Boolean);
  return (
    <th
      className={cn(
        "px-3 py-2 font-semibold leading-tight",
        align === "right" ? "text-right" : "text-left"
      )}
    >
      {words.length <= 1 ? (
        label
      ) : (
        <span className="inline-flex flex-col">
          {words.map((word, i) => (
            <span key={`${word}-${i}`}>{word}</span>
          ))}
        </span>
      )}
    </th>
  );
}

function MetricRow({ label, value, valueClassName }) {
  return (
    <div className="flex justify-between gap-3 text-xs">
      <span className="per-file-hint">{label}</span>
      <span className={cn("font-semibold text-foreground text-right", valueClassName)}>
        {value}
      </span>
    </div>
  );
}

function formatTransferQty(transferQty) {
  if (!Number.isFinite(transferQty)) return "—";
  if (transferQty === 0) return "0";
  return transferQty > 0 ? `+${transferQty}` : String(transferQty);
}

function resolveInventoryInId(row) {
  const direct = row?.inventoryInId;
  if (direct != null && String(direct).trim() !== "") return String(direct).trim();
  const raw = row?.raw;
  if (!raw || typeof raw !== "object") return "";
  const fromRaw =
    raw.inventoryInId ??
    raw.inventory_in_id ??
    raw.inventoryRecordId ??
    raw.inventory_record_id;
  return fromRaw != null && String(fromRaw).trim() !== ""
    ? String(fromRaw).trim()
    : "";
}

function resolveRemarks(row) {
  const direct = row?.remarks;
  if (direct != null && String(direct).trim() !== "") return String(direct).trim();
  const raw = row?.raw;
  if (!raw || typeof raw !== "object") return "";
  const fromRaw = raw.remarks ?? raw.remark;
  return fromRaw != null && String(fromRaw).trim() !== ""
    ? String(fromRaw).trim()
    : "";
}

/** @returns {"defective" | "inventory" | null} */
function getJournalImageMode(movementType) {
  const normalized = String(movementType || "")
    .trim()
    .toLowerCase();
  if (!normalized) return null;
  if (normalized.includes("defect")) return "defective";
  if (
    normalized === "receipt" ||
    normalized === "purchase from vendor" ||
    normalized.includes("oem warranty") ||
    normalized.includes("return")
  ) {
    return "inventory";
  }
  return null;
}

function resolveCustomerAllocationId(row) {
  const direct = row?.customerAllocationId;
  if (direct != null && String(direct).trim() !== "") {
    return String(direct).trim();
  }
  const raw = row?.raw;
  if (!raw || typeof raw !== "object") return "";
  const fromRaw =
    raw.customerAllocationId ??
    raw.customer_allocation_id ??
    raw.allocationId ??
    raw.allocation_id;
  return fromRaw != null && String(fromRaw).trim() !== ""
    ? String(fromRaw).trim()
    : "";
}

export function InventoryJournalTable({
  data,
  page,
  pageSize,
  onPageChange,
  totalItems,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rowMessage, setRowMessage] = useState("");
  const [imagesModal, setImagesModal] = useState(null);

  const resolvedTotalItems = totalItems ?? data.length;
  const totalPages = Math.max(1, Math.ceil(resolvedTotalItems / pageSize));
  const pagedRows = useMemo(() => {
    if (totalItems != null) return data;
    const start = (page - 1) * pageSize;
    return data.slice(start, start + pageSize);
  }, [data, page, pageSize, totalItems]);

  const formatDate = (isoStr) => {
    if (!isoStr) return "—";
    const dateObj = new Date(isoStr);
    const day = String(dateObj.getDate()).padStart(2, "0");
    const month = t(MONTH_KEYS[dateObj.getMonth()]);
    const year = dateObj.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const handleRowClick = (row) => {
    const isReservation = String(row.movementType || "")
      .trim()
      .toLowerCase()
      .includes("reservation");
    if (!isReservation) return;

    const allocationId = resolveCustomerAllocationId(row);

    if (!allocationId) {
      console.warn("Reservation journal row missing customerAllocationId", row.raw || row);
      setRowMessage(t("inventory.allocationIdMissingOnJournal"));
      return;
    }

    setRowMessage("");
    navigate(`/inventory-management/allocation-detail/${allocationId}`);
  };

  const openImagesModal = (e, row, mode) => {
    e?.stopPropagation?.();
    const inventoryInId = resolveInventoryInId(row);
    if (!inventoryInId) {
      setRowMessage(t("inventory.inventoryInIdMissingOnJournal"));
      return;
    }
    setRowMessage("");
    setImagesModal({ inventoryInId, mode });
  };

  const renderImagesButton = (row) => {
    const mode = getJournalImageMode(row.movementType);
    const inventoryInId = resolveInventoryInId(row);
    if (!mode || !inventoryInId) return null;

    const label =
      mode === "defective"
        ? t("inventory.viewDefectiveImages")
        : t("inventory.viewInventoryImages");

    return (
      <button
        type="button"
        onClick={(e) => openImagesModal(e, row, mode)}
        className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-(--color-text-border) bg-white text-primary hover:bg-gray-50 transition cursor-pointer text-[11px] font-bold"
      >
        <ImageIcon className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate max-w-[7.5rem]">{label}</span>
      </button>
    );
  };

  const renderRowActions = (row) => {
    const imagesButton = renderImagesButton(row);
    if (!imagesButton) return null;
    return (
      <div className="inline-flex items-center justify-end gap-1.5 flex-wrap">
        {imagesButton}
      </div>
    );
  };

  return (
    <div className="font-sans">
      <StockJournalImagesModal
        open={Boolean(imagesModal)}
        onOpenChange={(open) => {
          if (!open) setImagesModal(null);
        }}
        inventoryInId={imagesModal?.inventoryInId}
        mode={imagesModal?.mode || "inventory"}
      />

      {rowMessage ? (
        <div className="mx-4 mb-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs font-bold text-rose-700">
          {rowMessage}
        </div>
      ) : null}

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm min-w-[1080px]">
          <thead>
            <tr className={ui.tableHead}>
              <StackedHeader label={t("dateTime.date")} />
              <StackedHeader label={t("inventory.movementType")} />
              <StackedHeader label={t("common.to")} />
              <StackedHeader label={t("inventory.transferQuantity")} align="right" />
              <StackedHeader label={t("inventory.reservedQuantity")} align="right" />
              <StackedHeader label={t("common.from")} />
              <StackedHeader label={t("inventory.price")} align="right" />
              <StackedHeader label={t("inventory.batch")} />
              <StackedHeader label={t("common.actions")} align="right" />
            </tr>
          </thead>
          <tbody className="divide-y divide-(--color-text-border)/35">
            {pagedRows.map((row) => {
              const movementNorm = String(row.movementType || "")
                .trim()
                .toLowerCase();
              const isReservation = movementNorm.includes("reservation");
              const transferQty = Number(
                row.transferQuantity ?? row.quantity
              );
              const isPositive = transferQty > 0;
              const reservedQty =
                row.reservedQuantity != null && row.reservedQuantity !== ""
                  ? Number(row.reservedQuantity)
                  : null;
              const remarks = resolveRemarks(row);
              const rowTitle =
                remarks ||
                (isReservation ? t("inventory.allocationDetail") : undefined);
              return (
                <GreyTooltip key={row.id} content={rowTitle}>
                  <tr
                    onClick={() => handleRowClick(row)}
                    role={isReservation ? "button" : undefined}
                    tabIndex={isReservation ? 0 : undefined}
                    onKeyDown={(e) => {
                      if (!isReservation) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleRowClick(row);
                      }
                    }}
                    className={cn(
                      "hover:bg-workspace/20 transition-colors",
                      isReservation && "cursor-pointer"
                    )}
                  >
                    <td className="px-3 py-3.5 per-file-hint whitespace-nowrap">
                      {formatDate(row.date)}
                    </td>
                    <td className="px-3 py-3.5 whitespace-nowrap">
                      <MovementTypeBadge type={row.movementType} />
                    </td>
                    <td className="px-3 py-3.5 per-file-hint truncate">
                      {row.toName || "—"}
                    </td>
                    <td
                      className={cn(
                        "px-3 py-3.5 text-right font-semibold font-mono whitespace-nowrap",
                        isPositive ? "text-emerald-600" : "text-rose-600"
                      )}
                    >
                      {formatTransferQty(transferQty)}
                    </td>
                    <td className="px-3 py-3.5 text-right font-semibold font-mono whitespace-nowrap text-amber-600">
                      {reservedQty != null && Number.isFinite(reservedQty)
                        ? reservedQty
                        : "—"}
                    </td>
                    <td className="px-3 py-3.5 per-file-hint truncate">
                      {row.fromName || "—"}
                    </td>
                    <td className="px-3 py-3.5 text-right font-mono whitespace-nowrap text-foreground">
                      {formatPrice(row.purchasePricePerUnit)}
                    </td>
                    <td className="px-3 py-3.5 per-file-hint font-mono truncate">
                      {row.batchNumber || "—"}
                    </td>
                    <td className="px-3 py-3.5 text-right whitespace-nowrap">
                      {renderRowActions(row)}
                    </td>
                  </tr>
                </GreyTooltip>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <ul className="md:hidden space-y-2.5 p-3">
        {pagedRows.map((row) => {
          const movementNorm = String(row.movementType || "")
            .trim()
            .toLowerCase();
          const isReservation = movementNorm.includes("reservation");
          const transferQty = Number(row.transferQuantity ?? row.quantity);
          const isPositive = transferQty > 0;
          const reservedQty =
            row.reservedQuantity != null && row.reservedQuantity !== ""
              ? Number(row.reservedQuantity)
              : null;
          const rowActions = renderRowActions(row);
          const remarks = resolveRemarks(row);
          const rowTitle =
            remarks ||
            (isReservation ? t("inventory.allocationDetail") : undefined);

          return (
            <GreyTooltip key={row.id} content={rowTitle}>
              <li
                onClick={() => handleRowClick(row)}
                role={isReservation ? "button" : undefined}
                tabIndex={isReservation ? 0 : undefined}
                onKeyDown={(e) => {
                  if (!isReservation) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleRowClick(row);
                  }
                }}
                className={cn(
                  "rounded-xl border border-(--color-text-border)/60 bg-white px-3.5 py-3 space-y-2.5 shadow-xs",
                  isReservation && "active:bg-workspace/40 cursor-pointer"
                )}
              >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs per-file-hint">{formatDate(row.date)}</p>
                  <div className="mt-1.5">
                    <MovementTypeBadge type={row.movementType} />
                  </div>
                </div>
                <span
                  className={cn(
                    "text-sm font-extrabold font-mono shrink-0",
                    isPositive ? "text-emerald-600" : "text-rose-600"
                  )}
                >
                  {formatTransferQty(transferQty)}
                </span>
              </div>

              <div className="space-y-1.5 pt-1 border-t border-(--color-text-border)/25">
                <MetricRow label={t("common.to")} value={row.toName || "—"} />
                <MetricRow label={t("common.from")} value={row.fromName || "—"} />
                <MetricRow
                  label={t("inventory.reservedQuantity")}
                  value={
                    reservedQty != null && Number.isFinite(reservedQty)
                      ? reservedQty
                      : "—"
                  }
                  valueClassName="text-amber-600 font-mono"
                />
                <MetricRow
                  label={t("inventory.price")}
                  value={formatPrice(row.purchasePricePerUnit)}
                  valueClassName="font-mono"
                />
                <MetricRow
                  label={t("inventory.batch")}
                  value={row.batchNumber || "—"}
                  valueClassName="font-mono"
                />
              </div>

              {rowActions ? (
                <div
                  className="pt-1 border-t border-(--color-text-border)/25"
                  onClick={(e) => e.stopPropagation()}
                >
                  {rowActions}
                </div>
              ) : null}
              </li>
            </GreyTooltip>
          );
        })}
      </ul>

      <div className="px-4 py-3">
        <TablePagination
          page={page}
          totalPages={totalPages}
          totalItems={resolvedTotalItems}
          pageSize={pageSize}
          onPageChange={onPageChange}
        />
      </div>
    </div>
  );
}
